<script lang="ts" setup>
import { useField } from "vee-validate";
import { ExclamationCircleIcon } from "@heroicons/vue/solid";
import BaseInput from "./_base.vue";
import { vMaska } from "maska"

interface Props {
    type?: string;
    value?: string | number;
    name: string;
    label?: string;
    min?: number;
    max?: number;
    placeholder: string;
    autocomplete?: string;
    mask?: string;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    type: "text",
    value: undefined,
    name: undefined,
    label: undefined,
    placeholder: undefined,
    autocomplete: "",
    disabled: false,
});

const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange,
    meta,
} = useField(props.name, undefined, {
    initialValue: props.value,
});

function customHandleChange(e: Event) {
    const target = e.target;
    if (props.type === "number") {
        // @ts-ignore
        handleChange(Number(target?.value));
    } else {
        handleChange(e);
    }
}

defineExpose({ inputValue });
</script>

<template>
    <BaseInput :errorMessage="errorMessage" :meta="meta" :name="name" :label="label">
        <div class="relative z-[2]" :class="{ 'mt-2': label && label !== '' }">
            <span ref="prefix" class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 select-none"><slot name="prefix"></slot></span>
            <component
                :is="type !== 'textarea' ? 'input' : 'textarea'"
                class="block w-full appearance-none border border-gray-300 bg-gray-300 py-[.4375rem] px-[.9375rem] transition-colors placeholder:text-gray-800 focus:outline-none focus:ring-none md:p-[.9375rem]"
                :class="{
                    '!pl-[3.25rem]': $slots.prefix,
                    '!pr-[3.25rem]': $slots.suffix,
                    'bg-gray-100 pointer-events-none opacity-50': disabled,
                    'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500 focus:outline-none': errorMessage && meta.touched,
                    'min-h-[80px]': type === 'textarea',
                }"
                :name="name"
                :id="name"
                :type="type"
                :value="inputValue"
                :min="min"
                :max="max"
                :autocomplete="autocomplete"
                :placeholder="placeholder"
                v-maska
                :data-maska="mask ? mask : undefined"
                @input="customHandleChange"
                @blur="handleBlur"
            />
            <div v-if="errorMessage && meta.touched" class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ExclamationCircleIcon class="h-5 w-5 text-red" aria-hidden="true" />
            </div>
            <span ref="suffix" class="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 select-none"><slot name="suffix"></slot></span>
        </div>
    </BaseInput>
</template>
